import React, { useState, useEffect } from 'react';
import { useJobList, Job } from '../hooks/useJobStatus';
import { JobStatusModal } from '../components/JobStatusModal';
import { apiClient } from '../lib/api';
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  Pause,
  Trash2,
  RefreshCw,
  Eye,
  Filter
} from 'lucide-react';

export type JobQueueVariant = 'staff' | 'admin';

export interface JobQueueProps {
  /** `admin`: unter /admin/job-queue – alle Jobs, globales Kill (nur AdminRoute). */
  variant?: JobQueueVariant;
}

export const JobQueue: React.FC<JobQueueProps> = ({ variant = 'staff' }) => {
  const isAdminKillView = variant === 'admin';
  const { jobs, loading, error, refetch } = useJobList(isAdminKillView);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Auto-refresh alle 5 Sekunden
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [refetch]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'processing':
        return <Play className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'cancelled':
        return <Pause className="w-5 h-5 text-gray-600" />;
      case 'timeout':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Abgeschlossen';
      case 'failed': return 'Fehlgeschlagen';
      case 'processing': return 'In Bearbeitung';
      case 'pending': return 'Wartend';
      case 'cancelled': return 'Abgebrochen';
      case 'timeout': return 'Timeout';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      case 'processing': return 'text-blue-600 bg-blue-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'cancelled': return 'text-gray-600 bg-gray-50';
      case 'timeout': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'curricula_import': return 'Traditioneller Import';
      case 'curricula_unified_import': return 'Unified Import';
      case 'classwork_submission_analysis': return 'Klassenarbeit · Abgabe-Analyse';
      case 'classwork_reference_analysis': return 'Klassenarbeit · Referenz-Analyse';
      case 'classwork_correction': return 'Klassenarbeit · Korrektur';
      case 'classwork_submission_ocr': return 'Klassenarbeit · OCR';
      case 'classwork_reference_build': return 'Klassenarbeit · Referenzversion';
      case 'student_file_ingest': return 'Schülerdatei · Import';
      case 'student_file_backfill': return 'Schülerdatei · Backfill';
      default: return type;
    }
  };

  const getClassworkStageHint = (type: string) => {
    switch (type) {
      case 'classwork_submission_analysis':
        return 'Stage: Ingestion -> OCR/Vision -> Page Classification';
      case 'classwork_reference_analysis':
        return 'Stage: Referenzmaterial -> OCR/Vision -> Rubric-Kontext';
      case 'classwork_correction':
        return 'Stage: Routing -> Hybrid Scoring -> Policy Decision';
      case 'classwork_submission_ocr':
        return 'Stage: OCR Vorverarbeitung';
      case 'classwork_reference_build':
        return 'Stage: Referenzversion Build';
      default:
        return null;
    }
  };

  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const duration = endTime.getTime() - startTime.getTime();
    return Math.round(duration / 1000);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '–';
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '–';
    return d.toLocaleString('de-DE');
  };

  const handleCancelJob = async (e: React.MouseEvent, jobId: string) => {
    e.stopPropagation();
    const msg = isAdminKillView
      ? 'Diesen Job global killen? Er wird aus der Warteschlange entfernt bzw. abgebrochen. Laufende KI (z. B. Vision) stoppt nach der aktuellen Seite bzw. nach dem aktuellen Schritt.'
      : 'Diesen Job wirklich abbrechen? Eine laufende KI-Verarbeitung endet nach der aktuell bearbeiteten Seite.';
    if (!window.confirm(msg)) {
      return;
    }
    try {
      const response = await apiClient.delete(`/jobs/${jobId}`);
      const data = response.data ?? {};
      if (response.data?.success) {
        await refetch();
      } else {
        window.alert(data.error || 'Abbruch fehlgeschlagen');
      }
    } catch (err) {
      console.error(err);
      window.alert('Abbruch fehlgeschlagen');
    }
  };

  const filteredJobs = jobs.filter((job: Job) => {
    const statusMatch = statusFilter === 'all' || job.status === statusFilter;
    const typeMatch = typeFilter === 'all' || job.type === typeFilter;
    return statusMatch && typeMatch;
  });

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowJobModal(true);
  };

  const handleJobModalClose = () => {
    setShowJobModal(false);
    setSelectedJobId(null);
  };

  const handleJobComplete = (result: any) => {
    console.log('Job abgeschlossen:', result);
    refetch();
  };

  const getQueueStats = () => {
    const stats = {
      total: jobs.length,
      pending: jobs.filter((j: Job) => j.status === 'pending').length,
      processing: jobs.filter((j: Job) => j.status === 'processing').length,
      completed: jobs.filter((j: Job) => j.status === 'completed').length,
      failed: jobs.filter((j: Job) => j.status === 'failed').length,
      cancelled: jobs.filter((j: Job) => j.status === 'cancelled').length,
      timeout: jobs.filter((j: Job) => j.status === 'timeout').length
    };
    return stats;
  };

  const stats = getQueueStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Lade Job-Queue...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <XCircle className="w-5 h-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Fehler beim Laden der Job-Queue</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={() => refetch()}
              className="mt-2 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isAdminKillView ? 'Admin · Job-Queue' : 'Job-Queue Übersicht'}
        </h1>
        <p className="text-gray-600">
          {isAdminKillView
            ? 'Alle Hintergrund-Jobs des Mandanten (inkl. Redis-Correction). Wartende und laufende Jobs können Sie killen (Abbruch in der Queue; laufende Analysen enden kooperativ nach der aktuellen Seite).'
            : 'Ihre eigenen Hintergrund-Jobs. Wartende und laufende Jobs können Sie abbrechen.'}
        </p>
      </div>

      {/* Queue-Statistiken */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Gesamt</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          <div className="text-sm text-gray-600">Wartend</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.processing}</div>
          <div className="text-sm text-gray-600">Laufend</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
          <div className="text-sm text-gray-600">Abgeschlossen</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
          <div className="text-sm text-gray-600">Fehlgeschlagen</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-gray-600">{stats.cancelled}</div>
          <div className="text-sm text-gray-600">Abgebrochen</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-2xl font-bold text-orange-600">{stats.timeout}</div>
          <div className="text-sm text-gray-600">Timeout</div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filter:</span>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Alle Status</option>
            <option value="pending">Wartend</option>
            <option value="processing">Laufend</option>
            <option value="completed">Abgeschlossen</option>
            <option value="failed">Fehlgeschlagen</option>
            <option value="cancelled">Abgebrochen</option>
            <option value="timeout">Timeout</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Alle Typen</option>
            <option value="curricula_import">Traditionell</option>
            <option value="curricula_unified_import">Unified</option>
            <option value="classwork_submission_analysis">KA · Abgabe-Analyse</option>
            <option value="classwork_reference_analysis">KA · Referenz-Analyse</option>
            <option value="classwork_correction">KA · Korrektur</option>
            <option value="classwork_submission_ocr">KA · OCR</option>
            <option value="classwork_reference_build">KA · Referenzversion</option>
          </select>

          <button
            onClick={() => refetch()}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors flex items-center space-x-1"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Aktualisieren</span>
          </button>
        </div>
      </div>

      {/* Job-Liste */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Jobs ({filteredJobs.length})
          </h2>
        </div>

        {filteredJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Keine Jobs gefunden
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredJobs.map((job: Job) => (
              <div
                key={job.id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleJobClick(job.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {getStatusIcon(job.status)}
                    <div>
                      <div className="font-medium text-gray-900">
                        {getTypeText(job.type)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(job.createdAt || job.created_at)}
                        {isAdminKillView && job.userId != null ? (
                          <span className="block text-xs text-gray-500">User #{job.userId}</span>
                        ) : null}
                        {getClassworkStageHint(job.type) ? (
                          <span className="block text-xs text-indigo-600">{getClassworkStageHint(job.type)}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                        {getStatusText(job.status)}
                      </div>
                      {job.status === 'processing' && (
                        <div className="text-sm text-gray-600 mt-1">
                          {job.progress}% abgeschlossen
                        </div>
                      )}
                    </div>

                    {job.startedAt && (
                      <div className="text-sm text-gray-600">
                        {formatDuration(job.startedAt, job.completedAt)}s
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      {(job.status === 'pending' || job.status === 'processing') && (
                        <button
                          type="button"
                          onClick={(e) => void handleCancelJob(e, job.id)}
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-md transition-colors ${
                            isAdminKillView
                              ? 'text-white bg-red-600 hover:bg-red-700'
                              : 'text-red-700 bg-red-50 hover:bg-red-100'
                          }`}
                          title={isAdminKillView ? 'Job killen (global abbrechen)' : 'Job abbrechen'}
                        >
                          {isAdminKillView ? <Trash2 className="w-3.5 h-3.5" /> : null}
                          {isAdminKillView ? 'Kill' : 'Abbrechen'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJobClick(job.id);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Details anzeigen"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {job.status === 'processing' && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${job.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {job.error && (
                  <div className="mt-2 text-sm text-red-600">
                    Fehler: {job.error}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Job Status Modal */}
      <JobStatusModal
        jobId={selectedJobId}
        isOpen={showJobModal}
        onClose={handleJobModalClose}
        onComplete={handleJobComplete}
      />
    </div>
  );
};
